/**
 * Krok 7: Gotowe — publikacja profilu firmy.
 * Buduje checklistę pól firmy i renderuje wspólny WizardSummaryStep (1:1 z resztą kreatorów).
 */
import WizardSummaryStep from '@/components/wizard/WizardSummaryStep';
import FontStylePicker from '@/components/wizard/FontStylePicker';
import LivePagePreview from '@/components/wizard/LivePagePreview';
import ThemePicker from '@/components/wizard/ThemePicker';
import AccentColorPicker from '@/components/wizard/AccentColorPicker';
import { industryName } from '../industries';

const DESC_MIN = 120;

export default function Step7Done({
  data, updateData, termsAccepted, setTermsAccepted,
  goToStep, onPublish, onSaveLater, submitting, isEdit,
}) {
  const name = (data.name || '').trim();
  const industry = data.industry;
  const city = (data.city || '').trim();
  const location = [data.city, data.address].filter(Boolean).join(', ');
  const desc = (data.description || '').trim();
  const services = data.services || [];
  const portfolio = data.portfolio || [];
  const reviews = data.reviews || [];
  const contact = [data.phone, data.email, data.website].filter(Boolean);

  const checklist = [
    { icon: 'briefcase', label: 'Nazwa firmy', ok: !!name, value: name || 'Uzupełnij nazwę', hint: 'Tak Twoja firma pojawi się w katalogu i wyszukiwarce.', step: 1, required: true },
    { icon: 'star', label: 'Branża', ok: !!industry, value: industry ? industryName(industry) : 'Wybierz branżę', hint: 'Dzięki branży trafisz do właściwej kategorii katalogu.', step: 1, required: true },
    { icon: 'map', label: 'Lokalizacja', ok: !!city, value: location || 'Uzupełnij lokalizację', hint: 'Klienci szukają usług w swojej okolicy.', step: 1, required: true },
    { icon: 'text', label: 'Opis firmy', ok: desc.length >= DESC_MIN, value: desc ? (desc.length < DESC_MIN ? `Krótki opis (${desc.length} znaków)` : 'Uzupełniony') : 'Brak opisu', hint: 'Opisz, czym się zajmujesz i co Cię wyróżnia.', step: 2, addLabel: 'Uzupełnij' },
    { icon: 'services', label: 'Usługi i cennik', ok: services.length > 0, value: services.length ? `${services.length} pozycji` : 'Brak usług', hint: 'Pokaż ofertę i ceny — klienci chętniej piszą, gdy wiedzą czego się spodziewać.', step: 3, addLabel: 'Dodaj' },
    { icon: 'gallery', label: 'Realizacje (portfolio)', ok: portfolio.length > 0, value: portfolio.length ? `${portfolio.length} zdjęć` : 'Brak zdjęć', hint: 'Zdjęcia realizacji budują zaufanie najszybciej.', step: 4, addLabel: 'Dodaj' },
    { icon: 'star', label: 'Opinie klientów', ok: reviews.length > 0, value: reviews.length ? `${reviews.length} opinii` : 'Brak opinii', hint: 'Nawet kilka opinii znacząco zwiększa liczbę zapytań.', step: 5, addLabel: 'Dodaj' },
    { icon: 'phone', label: 'Dane kontaktowe', ok: contact.length > 0, value: contact.length ? contact.slice(0, 2).join(' · ') : 'Dodaj telefon lub e-mail', hint: 'Bez kontaktu klienci nie wyślą Ci zapytania.', step: 6 },
  ];

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
        <p className="text-sm font-semibold text-gray-900">Dostosuj wygląd swojej strony</p>
        <p className="text-xs text-gray-600 mt-0.5">Masz do wyboru: <span className="font-semibold">styl pisma</span>, <span className="font-semibold">motyw</span> i <span className="font-semibold">kolor akcentu</span>. Podgląd po prawej zmienia się na żywo.</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <FontStylePicker value={data.font_style} onChange={(v) => updateData({ font_style: v })} />
          <ThemePicker
            value={data.theme}
            onChange={(v) => updateData({ theme: v })}
            title="Motyw"
            desc="Jasne lub ciemne tło Twojej strony."
            options={[
              { id: 'dark', label: 'Ciemny', swatch: '#060606' },
              { id: 'light', label: 'Jasny', swatch: '#faf9f6' },
            ]}
          />
          <AccentColorPicker value={data.accent_color} onChange={(v) => updateData({ accent_color: v })} />
        </div>
        <div className="lg:sticky lg:top-4 self-start">
          <LivePagePreview
            fontStyle={data.font_style}
            eyebrow={industry ? industryName(industry) : 'Firma'}
            title={name || 'Twoja firma'}
            subtitle={location}
            image={(portfolio && portfolio[0]) || ''}
            accentKey={data.accent_color || 'gold'}
            theme={data.theme || 'dark'}
          />
        </div>
      </div>
      <WizardSummaryStep
        headerTitle="Twoja strona firmy jest gotowa do publikacji"
      headerSubtitle="Po publikacji Twoja firma pojawi się w katalogu APISAI i otrzymasz własną stronę z formularzem kontaktowym."
      pageCardTitle="Strona firmy"
      checklist={checklist}
      goToStep={goToStep}
      catalogChecked={data.show_in_catalog}
      onCatalogChange={(v) => updateData({ show_in_catalog: v })}
      catalogLabel="Chcę, aby moja firma była widoczna w katalogu APISAI."
      catalogTestId="bwv2-catalog-toggle"
      termsAccepted={termsAccepted}
      setTermsAccepted={setTermsAccepted}
      termsTestId="bwv2-terms-checkbox"
      dataAccurateTestId="bwv2-data-accurate"
      onPublish={onPublish}
      onSaveLater={onSaveLater}
      submitting={submitting}
      isEdit={isEdit}
      publishLabel="Opublikuj stronę firmy"
      testId="business-wizard-v2-step-done"
      />
    </div>
  );
}
